import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, AlertCircle, CheckCircle, LoaderIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { submitForm } from '../utils/submitForm';
type CompanyFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
export function CompanyFormModal({
  isOpen,
  onClose
}: CompanyFormModalProps) {
  const {
    t,
    language
  } = useLanguage();
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [positions, setPositions] = useState('');
  const [crewNumber, setCrewNumber] = useState('');
  const [location, setLocation] = useState('');
  const [additional, setAdditional] = useState('');
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  const vesselTypes = t('companies.form.vesselTypes');
  const regions = t('companies.form.regions');
  const durations = t('companies.form.durations');
  const rankOptions = [{
    category: t('specialists.form.ranks.deck'),
    options: [t('specialists.form.ranks.options.master'), t('specialists.form.ranks.options.chiefMate'), t('specialists.form.ranks.options.secondMate'), t('specialists.form.ranks.options.thirdMate'), t('specialists.form.ranks.options.bosun'), t('specialists.form.ranks.options.ab'), t('specialists.form.ranks.options.os')]
  }, {
    category: t('specialists.form.ranks.engine'),
    options: [t('specialists.form.ranks.options.chiefEng'), t('specialists.form.ranks.options.secondEng'), t('specialists.form.ranks.options.thirdEng'), t('specialists.form.ranks.options.fourthEng'), t('specialists.form.ranks.options.eto'), t('specialists.form.ranks.options.asstEto'), t('specialists.form.ranks.options.motorman'), t('specialists.form.ranks.options.wiper')]
  }, {
    category: t('specialists.form.ranks.other'),
    options: [t('specialists.form.ranks.options.dpo'), t('specialists.form.ranks.options.sdpo'), t('specialists.form.ranks.options.craneOp'), t('specialists.form.ranks.options.rigger'), t('specialists.form.ranks.options.electrician'), t('specialists.form.ranks.options.mechanic'), t('specialists.form.ranks.options.other')]
  }];
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat' as const,
    backgroundPosition: 'right 12px center'
  };
  const resetForm = () => {
    setCompanyName('');
    setContactPerson('');
    setEmail('');
    setPhone('');
    setVesselType('');
    setPositions('');
    setCrewNumber('');
    setLocation('');
    setAdditional('');
    setStartDate('');
    setDuration('');
    setAgreedToTerms(false);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms || submitting) return;
    setSubmitting(true);
    setSubmitStatus('idle');
    const messageParts: string[] = [];
    if (vesselType) messageParts.push(`Vessel: ${vesselType}`);
    if (positions) messageParts.push(`Positions: ${positions}`);
    if (crewNumber) messageParts.push(`Crew: ${crewNumber}`);
    if (location) messageParts.push(`Region: ${location}`);
    if (startDate) messageParts.push(`Start: ${startDate}`);
    if (duration) messageParts.push(`Duration: ${duration}`);
    if (additional) messageParts.push(`Req: ${additional.slice(0, 300)}`);
    const fd = new FormData();
    fd.append('name', companyName);
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', messageParts.join(' | '));
    try {
      const result = await submitForm('companies', fd, language);
      if (result.ok) {
        setSubmitStatus('success');
        resetForm();
        setTimeout(() => {
          setSubmitStatus('idle');
          onClose();
        }, 2500);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 6000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 6000);
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(<div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-85">
      <div ref={modalRef} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-8" role="dialog" aria-modal="true" data-id="element-86">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between" data-id="element-87">
          <h2 className="text-xl font-bold text-[var(--color-text-dark)]" data-id="element-88">
            {t('companies.form.title')}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-89">
            <X className="w-5 h-5 text-gray-500" data-id="element-90" />
          </button>
        </div>

        {/* Success overlay */}
        {submitStatus === 'success' && <div className="absolute inset-0 z-20 bg-white/95 rounded-2xl flex flex-col items-center justify-center gap-3" data-id="element-91">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center" data-id="element-92">
              <CheckCircle className="w-8 h-8 text-green-600" data-id="element-93" />
            </div>
            <p className="text-lg font-semibold text-[var(--color-text-dark)]" data-id="element-94">
              {t('formStatus.sent')}
            </p>
          </div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-95">
          {/* Company Information */}
          <div data-id="element-96">
            <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-3" data-id="element-97">
              {t('companies.form.sections.company')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-98">
              <div data-id="element-99">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-100">
                  {t('companies.form.labels.companyName')}{' '}
                  <span className="text-red-500" data-id="element-101">*</span>
                </label>
                <input type="text" required value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-102" />
              </div>
              <div data-id="element-103">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-104">
                  {t('companies.form.labels.contactPerson')}{' '}
                  <span className="text-red-500" data-id="element-105">*</span>
                </label>
                <input type="text" required value={contactPerson} onChange={e => setContactPerson(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-106" />
              </div>
              <div data-id="element-107">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-108">
                  {t('companies.form.labels.email')}{' '}
                  <span className="text-red-500" data-id="element-109">*</span>
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-110" />
              </div>
              <div data-id="element-111">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-112">
                  {t('companies.form.labels.phone')}{' '}
                  <span className="text-red-500" data-id="element-113">*</span>
                </label>
                <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-114" />
              </div>
            </div>
          </div>

          {/* Request Details */}
          <div data-id="element-115">
            <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-3" data-id="element-116">
              {t('companies.form.sections.details')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-117">
              <div data-id="element-118">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-119">
                  {t('companies.form.labels.vesselType')}{' '}
                  <span className="text-red-500" data-id="element-120">*</span>
                </label>
                <select required value={vesselType} onChange={e => setVesselType(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-121">
                  <option value="" data-id="element-122">
                    {t('companies.form.labels.selectVessel')}
                  </option>
                  {vesselTypes.map((type: string) => <option key={type} value={type} data-id="element-123">
                      {type}
                    </option>)}
                </select>
              </div>
              <div data-id="element-124">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-125">
                  {t('companies.form.labels.positions')}{' '}
                  <span className="text-red-500" data-id="element-126">*</span>
                </label>
                <select required value={positions} onChange={e => setPositions(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-127">
                  <option value="" data-id="element-128">
                    {t('companies.form.labels.selectPositions')}
                  </option>
                  {rankOptions.map(group => <optgroup key={group.category} label={group.category} data-id="element-129">
                      {group.options.map(option => <option key={option} value={option} data-id="element-130">
                          {option}
                        </option>)}
                    </optgroup>)}
                </select>
              </div>
              <div data-id="element-131">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-132">
                  {t('companies.form.labels.crewNumber')}{' '}
                  <span className="text-red-500" data-id="element-133">*</span>
                </label>
                <input type="number" required min="1" placeholder="e.g., 5" value={crewNumber} onChange={e => setCrewNumber(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-134" />
              </div>
              <div data-id="element-135">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-136">
                  {t('companies.form.labels.location')}
                </label>
                <select value={location} onChange={e => setLocation(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-137">
                  <option value="" data-id="element-138">
                    {t('companies.form.labels.selectLocation')}
                  </option>
                  {regions.map((region: string) => <option key={region} value={region} data-id="element-139">
                      {region}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="mt-4" data-id="element-140">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-141">
                {t('companies.form.labels.additional')}
              </label>
              <textarea value={additional} onChange={e => setAdditional(e.target.value)} rows={3} placeholder={t('companies.form.labels.additionalPlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none" data-id="element-142" />
            </div>
          </div>

          {/* Timeline */}
          <div data-id="element-143">
            <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-3" data-id="element-144">
              {t('companies.form.sections.timeline')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-145">
              <div data-id="element-146">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-147">
                  {t('companies.form.labels.startDate')}{' '}
                  <span className="text-red-500" data-id="element-148">*</span>
                </label>
                <input type="date" required value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-149" />
              </div>
              <div data-id="element-150">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-151">
                  {t('companies.form.labels.duration')}
                </label>
                <select value={duration} onChange={e => setDuration(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-152">
                  <option value="" data-id="element-153">
                    {t('companies.form.labels.selectDuration')}
                  </option>
                  <option value="1-month" data-id="element-154">{durations.m1}</option>
                  <option value="2-months" data-id="element-155">{durations.m2}</option>
                  <option value="3-months" data-id="element-156">{durations.m3}</option>
                  <option value="4-months" data-id="element-157">{durations.m4}</option>
                  <option value="6-months" data-id="element-158">{durations.m6}</option>
                  <option value="9-months" data-id="element-159">{durations.m9}</option>
                  <option value="12-months" data-id="element-160">{durations.m12}</option>
                  <option value="ongoing" data-id="element-161">{durations.ongoing}</option>
                  <option value="project-based" data-id="element-162">{durations.project}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="pt-2" data-id="element-163">
            <label className="flex items-start gap-3 cursor-pointer" data-id="element-164">
              <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-[var(--color-tan)] border-gray-300 rounded focus:ring-[var(--color-tan)]" data-id="element-165" />
              <span className="text-sm text-[var(--color-text-dark)]" data-id="element-166">
                {t('companies.form.labels.consent')}{' '}
                <span className="text-red-500" data-id="element-167">*</span>
              </span>
            </label>
          </div>

          {/* Error message */}
          {submitStatus === 'error' && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-168">
              <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-169" />
              <span data-id="element-170">{t('formStatus.error')}</span>
            </div>}

          <div className="flex justify-center pt-2" data-id="element-171">
            <button type="submit" disabled={!agreedToTerms || submitting} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-all flex items-center gap-2 ${agreedToTerms && !submitting ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-172">
              {submitting ? <>
                  <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-173" />
                  {t('formStatus.sending')}
                </> : <>
                  <CheckCircle className="w-5 h-5" data-id="element-174" />
                  {t('companies.form.labels.submit')}
                </>}
            </button>
          </div>

          <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-175">
            {t('companies.form.labels.disclaimer')}
          </p>
        </form>
      </div>
    </div>, document.body);
}